import React, { useContext, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { I18nContext } from '../../context/translations/TranslationsContext.context';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '../ui/dialog';
import { toast } from '../ui/sonner';

import PasswordSubmissionOutcome from './PasswordSubmissionOutcome';

export interface SecurityWarningModalV2Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onChangePassword: (password: string) => Promise<void> | void;
}

// Obligatorio: el usuario entró por magic link teniendo ya contraseña, así que
// por seguridad debe establecer una nueva antes de continuar (ver LoginFormV2 —
// lo dispara `mustSetPassword` en el JWT del canje). No hay botón de
// "ahora no": ni la X ni Escape/click fuera cierran el modal (ver DialogContent
// más abajo), solo el envío correcto del formulario.
const SecurityWarningModalV2 = ({
	open,
	onOpenChange,
	onChangePassword
}: SecurityWarningModalV2Props) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	// El resultado del envío se muestra cambiando el estado de este mismo modal (nunca
	// abriendo un modal nuevo encima) — ver PasswordSubmissionOutcome.
	const [submissionState, setSubmissionState] = useState<'form' | 'success' | 'error'>('form');

	const resetState = () => {
		setPassword('');
		setConfirmPassword('');
		setShowPassword(false);
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!password || !confirmPassword) {
			toast.error(translate('general.securityWarningModal.passwordRequiredError'));
			return;
		}

		if (password !== confirmPassword) {
			toast.error(translate('general.securityWarningModal.passwordMismatchError'));
			return;
		}

		setIsSubmitting(true);
		try {
			await onChangePassword(password);
			resetState();
			setSubmissionState('success');
		} catch (error) {
			console.error('Error al actualizar la contraseña:', error);
			setSubmissionState('error');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleContinue = () => {
		onOpenChange(false);
		setSubmissionState('form');
	};

	const handleRetry = () => setSubmissionState('form');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="[&>button]:hidden"
				onEscapeKeyDown={(event) => event.preventDefault()}
				onPointerDownOutside={(event) => event.preventDefault()}
			>
				<div className="individual-v2 -m-6 p-6 rounded-lg">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{submissionState === 'success'
								? translate('general.securityWarningModal.successTitle')
								: submissionState === 'error'
									? translate('general.securityWarningModal.errorTitle')
									: translate('general.securityWarningModal.title')}
						</DialogTitle>
						<DialogDescription>
							{submissionState === 'success'
								? translate('general.securityWarningModal.successMessage')
								: submissionState === 'error'
									? translate('general.securityWarningModal.errorMessage')
									: translate('general.securityWarningModal.description')}
						</DialogDescription>
					</DialogHeader>

					{submissionState !== 'form' ? (
						<PasswordSubmissionOutcome
							variant={submissionState}
							buttonLabel={translate(
								submissionState === 'success'
									? 'general.securityWarningModal.continueButton'
									: 'general.securityWarningModal.retryButton'
							)}
							onAction={submissionState === 'success' ? handleContinue : handleRetry}
						/>
					) : (
						<form onSubmit={handleSubmit} className="space-y-4 mt-4">
							<div className="space-y-2">
								<Label htmlFor="security-warning-password">
									{translate('general.login.password')}
								</Label>
								<div className="relative">
									<Input
										id="security-warning-password"
										name="password"
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="••••••••"
										className="h-11 pr-10"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
										aria-label={
											showPassword
												? translate('general.password.hide')
												: translate('general.password.show')
										}
									>
										{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
									</button>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="security-warning-password-confirm">
									{translate('general.securityWarningModal.confirmPasswordLabel')}
								</Label>
								<Input
									id="security-warning-password-confirm"
									name="confirmPassword"
									type={showPassword ? 'text' : 'password'}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="••••••••"
									className="h-11"
								/>
							</div>

							<DialogFooter className="flex flex-col gap-2 sm:flex-col">
								<Button
									type="submit"
									className="w-full h-11 bg-arca-dark-green hover:bg-arca-dark-green/90 text-white font-medium"
									disabled={isSubmitting}
								>
									{isSubmitting ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											{translate('general.securityWarningModal.submitButtonLoading')}
										</>
									) : (
										translate('general.securityWarningModal.submitButton')
									)}
								</Button>
							</DialogFooter>

							<p className="mt-2 text-center text-xs text-muted-foreground">
								{translate('general.securityWarningModal.footerNote')}
							</p>
						</form>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default SecurityWarningModalV2;
